import { Badge } from "@/components/ui/badge";
import type { DemoSpace, DemoSpaceStatus } from "@/features/admin";
import type { Tone } from "@/features/utils/colors";
import { useDateFnsLocale } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { RetryButton } from "./retry-button";

/** The landing site a space belongs to, the one its flyer comes from. */
const NICHE_LABELS: Record<string, string> = {
  beauty: "Beauté",
  drive: "Auto-école",
  healthcare: "Santé",
  realestate: "Immobilier",
};

const STATUS: Record<DemoSpaceStatus, { label: string; tone: Tone }> = {
  pending: { label: "En attente", tone: "gray" },
  running: { label: "En cours", tone: "blue" },
  done: { label: "Prêt", tone: "green" },
  failed: { label: "Échec", tone: "red" },
};

const STEPS: Record<string, string> = {
  tenant: "création de l'espace",
  site: "lecture du site",
  setup: "configuration",
  services: "services",
  team: "équipe",
  done: "",
};

/** What the row says about how far it got: a step while it runs, the reason when it stopped. */
const progress = (space: DemoSpace) => {
  if (space.status === "failed") return space.error || "—";
  if (space.status === "running") return STEPS[space.step] || "";
  return space.error; // a ready space can still carry what was left out
};

/** Has the prospect called? How many times, and when last. */
const Calls = ({ space }: { space: DemoSpace }) => {
  const locale = useDateFnsLocale();
  if (!space.tenant_id) return <>—</>;
  if (!space.calls_count) {
    return <span className="text-muted-foreground">0</span>;
  }
  return (
    <div>
      <div className="font-medium tabular-nums">{space.calls_count}</div>
      {space.last_call_at > 0 && (
        <div className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(space.last_call_at), {
            addSuffix: true,
            locale,
          })}
        </div>
      )}
    </div>
  );
};

export const columns: ColumnDef<DemoSpace>[] = [
  {
    accessorKey: "website",
    header: "Entreprise",
    cell: ({ row }) => {
      const space = row.original;
      const name = space.tenant_name || space.business_name || space.website;
      return (
        <div className="min-w-0">
          {space.tenant_id ? (
            <Link
              to={`/admin/clients/${space.tenant_id}`}
              className="font-medium hover:underline"
            >
              {name}
            </Link>
          ) : (
            <span className="font-medium">{name}</span>
          )}
          <div className="truncate text-xs text-muted-foreground">
            {space.website || "sans site"}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "industry",
    header: "Secteur",
    cell: ({ row }) => (
      <div className="min-w-0">
        <span>{row.original.industry || "—"}</span>
        <div className="truncate text-xs text-muted-foreground">
          {NICHE_LABELS[row.original.niche] || "sans site de niche"}
        </div>
      </div>
    ),
  },
  {
    id: "contact",
    header: "Contact",
    cell: ({ row }) =>
      [row.original.first_name, row.original.last_name]
        .filter(Boolean)
        .join(" ") || "—",
  },
  {
    accessorKey: "handover_code",
    header: "Code",
    cell: ({ row }) => (
      <span className="font-mono tabular-nums">
        {row.original.handover_code || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "État",
    cell: ({ row }) => {
      const { label, tone } = STATUS[row.original.status];
      return <Badge color={tone}>{label}</Badge>;
    },
  },
  {
    // Bookable and quotable first: a service with no duration or no price is one
    // the agent can name but neither book nor price.
    accessorKey: "services_complete_count",
    header: "Services",
    cell: ({ row }) => {
      const space = row.original;
      if (space.status !== "done") return "—";
      return (
        <div title="Avec durée et prix / au total">
          <span className="font-medium tabular-nums">
            {space.services_complete_count}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {" "}
            / {space.services_count}
          </span>
          {space.services_fictional && (
            <div className="text-xs text-muted-foreground">fictifs</div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "team_count",
    header: "Équipe",
    cell: ({ row }) => {
      const space = row.original;
      if (space.status !== "done") return "—";
      return (
        <div>
          <span className="tabular-nums">{space.team_count}</span>
          {space.team_fictional && (
            <div className="text-xs text-muted-foreground">fictive</div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "calls_count",
    header: "Appels",
    cell: ({ row }) => <Calls space={row.original} />,
  },
  {
    id: "progress",
    header: "Détail",
    cell: ({ row }) => (
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {progress(row.original)}
      </span>
    ),
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) =>
      row.original.status === "failed" ||
      (row.original.status === "done" && row.original.error) ? (
        <div className="text-right">
          <RetryButton id={row.original.id} />
        </div>
      ) : null,
  },
];
